import DEPLOYMENT_CONFIG from '../config/deployment';

class PartnershipApiService {
  private getAuthHeaders(includeContentType: boolean = false) {
    const token = localStorage.getItem('access_token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      'X-User-Id': user.id?.toString() || ''
    };
    
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
    
    return headers;
  }

  private async makeRequest(url: string, options: RequestInit = {}) {
    // Only include Content-Type for requests with a body
    const hasBody = options.body !== undefined;
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getAuthHeaders(hasBody),
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Notification methods
  async getNotifications(userId: number) {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/notifications?user_id=${userId}`
    );
  }

  async markNotificationAsRead(notificationId: number) {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/notifications/${notificationId}/read`,
      { method: 'POST' }
    );
  }

  async markAllNotificationsAsRead(userId: number) {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/notifications/read-all`,
      {
        method: 'POST',
        body: JSON.stringify({ user_id: userId })
      }
    );
  }

  async getChatUnreadCount() {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/chat/notifications/unread`
    );
  }

  // Main page content methods
  async getMainPageContent() {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/main-page-content`
    );
  }

  async updateMainPageContent(content: any) {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/main-page-content`,
      {
        method: 'PUT',
        body: JSON.stringify(content)
      }
    );
  }

  // Leaderboard methods
  async getLeaderboard() {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/leaderboard/top-ten`
    );
  }

  // Task methods
  async getTasks() {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/tasks`
    );
  }

  async getTaskStatistics() {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/tasks/statistics`
    );
  }

  async createTask(taskData: any) {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/tasks`,
      {
        method: 'POST',
        body: JSON.stringify(taskData)
      }
    );
  }

  async updateTask(taskId: number, taskData: any) {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/tasks/${taskId}`,
      {
        method: 'PUT',
        body: JSON.stringify(taskData)
      }
    );
  }

  async deleteTask(taskId: number) {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/tasks/${taskId}`,
      { method: 'DELETE' }
    );
  }

  async completeTask(taskId: number, completionData: any) {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/tasks/${taskId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify(completionData)
      }
    );
  }

  // Lead methods
  async getLeads() {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/leads`
    );
  }

  async createLead(leadData: any) {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/leads`,
      {
        method: 'POST',
        body: JSON.stringify(leadData)
      }
    );
  }

  // User methods
  async getUsers() {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/users`
    );
  }

  async getCurrentUser() {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/users/me`
    );
  }

  async updateCurrentUser(userData: any) {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/users/me`,
      {
        method: 'PUT',
        body: JSON.stringify(userData)
      }
    );
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const requestData: any = {
      new_password: newPassword
    };
    
    // Only include current_password if it's provided
    if (currentPassword && currentPassword.trim() !== '') {
      requestData.current_password = currentPassword;
    }

    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/users/me`,
      {
        method: 'PUT',
        body: JSON.stringify(requestData)
      }
    );
  }

  async uploadAvatar(file: File) {
    const formData = new FormData();
    formData.append('avatar', file);
    
    const response = await fetch(`${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/users/me/avatar`, {
      method: 'POST',
      headers: this.getAuthHeaders(false), // Don't include Content-Type for FormData
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Dashboard methods
  async getDashboard() {
    return this.makeRequest(
      `${DEPLOYMENT_CONFIG.getBackendUrl()}/partnership/api/dashboard`
    );
  }
}

const partnershipApiService = new PartnershipApiService();
export default partnershipApiService;
